import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react'
import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ContentFChatInputProps {
  onSubmit: (content: string) => void
  disabled?: boolean
  placeholder?: string
}

export function ContentFChatInput({ onSubmit, disabled, placeholder }: ContentFChatInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollPositionRef = useRef<number>(0)

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [value])

  // Save scroll position before focus (iOS keyboard fix)
  const handleFocus = useCallback(() => {
    scrollPositionRef.current = window.scrollY
    // Use requestAnimationFrame to restore scroll after iOS keyboard causes unwanted scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollPositionRef.current) > 50) {
          window.scrollTo(0, scrollPositionRef.current)
        }
      })
    })
  }, [])

  const handleSubmit = useCallback(() => {
    if (value.trim() && !disabled) {
      onSubmit(value.trim())
      setValue('')
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
      // Blur on mobile after submit to hide keyboard
      if (window.innerWidth < 768) {
        textareaRef.current?.blur()
      }
    }
  }, [value, disabled, onSubmit])

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        disabled={disabled}
        placeholder={placeholder || "Type a message..."}
        rows={1}
        className={cn(
          'w-full resize-none rounded-lg border border-border bg-background pl-4 pr-12 py-3',
          'text-base md:text-sm text-foreground placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-1 focus:ring-foreground/20',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors'
        )}
      />

      {/* Send button - right side */}
      <button
        onClick={handleSubmit}
        disabled={disabled || !value.trim()}
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2',
          'p-3 -m-1 rounded-md',
          'text-muted-foreground hover:text-foreground active:text-foreground',
          'disabled:opacity-30 disabled:cursor-not-allowed',
          'transition-colors'
        )}
        aria-label="Send message"
      >
        <Send className="w-5 h-5 md:w-4 md:h-4" strokeWidth={1.5} />
      </button>
    </div>
  )
}
